import {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import {
  getPdfPageScrollTop,
  pickMostVisiblePage
} from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import {
  IAssetViewerStateContextValue,
  ShapesDrawingToolsProps
} from '@context/AssetViewerStateContext';

type UsePdfScrollParams = {
  wrapperRef: RefObject<OverlayScrollbarsComponentRef<'div'>>;
  pageRefsMap: MutableRefObject<Map<number, HTMLDivElement>>;
  skipActivePageScrollSyncRef: MutableRefObject<boolean>;
  pagesArray: { page: number }[];
  pagesCount: number;
  /** Rendered height of every page, pages may differ in size */
  pageHeights: number[];
  /** The page under the cursor is the active one, not the most visible */
  isActivePageFollowingPointer?: boolean;
  isPageSizeReady: boolean;
  pageRefsVersion: number;
  activePageNumber: number;
  selectedCommentId: string | null;
  shapesDrawingTools: ShapesDrawingToolsProps;
  runCanvasLayout: () => void;
  setActivePageNumber: IAssetViewerStateContextValue['setActivePageNumber'];
  selectComment: IAssetViewerStateContextValue['selectComment'];
  setDocumentHighlight: IAssetViewerStateContextValue['setDocumentHighlight'];
  setCanvasMode: IAssetViewerStateContextValue['setCanvasMode'];
};

// Pages rendered before and after the ones in view: each page is a canvas as
// large as the page (by the pixel ratio), so only a few around the view
const renderBuffer = 2;

export function usePdfScroll({
  wrapperRef,
  pageRefsMap,
  skipActivePageScrollSyncRef,
  pagesArray,
  pagesCount,
  pageHeights,
  isActivePageFollowingPointer = false,
  isPageSizeReady,
  pageRefsVersion,
  activePageNumber,
  selectedCommentId,
  shapesDrawingTools,
  runCanvasLayout,
  setActivePageNumber,
  selectComment,
  setDocumentHighlight,
  setCanvasMode
}: UsePdfScrollParams) {
  const activePageFromScrollRef = useRef(false);
  const lastScrolledPageRef = useRef(activePageNumber);
  const pendingScrollToPageRef = useRef<number | null>(null);

  const [renderRange, setRenderRange] = useState({
    start: 1,
    end: 1 + renderBuffer
  });
  const [visiblePageNumber, setVisiblePageNumber] = useState(activePageNumber);

  useEffect(() => {
    setVisiblePageNumber(activePageNumber);
  }, [activePageNumber]);

  const scrollPdfToPage = useCallback(
    (pageNumber: number) => {
      const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
      const pageEl = pageRefsMap.current.get(pageNumber);
      if (!viewport || !pageEl) {
        pendingScrollToPageRef.current = pageNumber;
        return false;
      }

      pendingScrollToPageRef.current = null;

      skipActivePageScrollSyncRef.current = true;
      viewport.scrollTo({
        top: getPdfPageScrollTop(viewport, pageEl),
        behavior: 'instant'
      });
      window.setTimeout(() => {
        skipActivePageScrollSyncRef.current = false;
      }, 500);
      return true;
    },
    [wrapperRef, pageRefsMap, skipActivePageScrollSyncRef]
  );

  const updateActivePageFromScroll = useCallback(() => {
    if (skipActivePageScrollSyncRef.current) return;

    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return;

    const nextPage = pickMostVisiblePage(
      pagesArray,
      pageRefsMap.current,
      viewport
    );
    if (!nextPage) return;

    setVisiblePageNumber(nextPage);

    if (selectedCommentId || isActivePageFollowingPointer) return;
    if (nextPage === activePageNumber) return;

    activePageFromScrollRef.current = true;
    lastScrolledPageRef.current = nextPage;
    setActivePageNumber(nextPage);
    if (shapesDrawingTools.shape !== 'documentHighlight') {
      selectComment(null);
      setDocumentHighlight(null);
      setCanvasMode('view');
    }
  }, [
    wrapperRef,
    pageRefsMap,
    skipActivePageScrollSyncRef,
    pagesArray,
    activePageNumber,
    shapesDrawingTools.shape,
    selectedCommentId,
    isActivePageFollowingPointer,
    setActivePageNumber,
    selectComment,
    setDocumentHighlight,
    setCanvasMode
  ]);

  // Top of every page in the scrolled content
  const pageOffsets = useMemo(() => {
    const padTop = 8;
    const gap = 8;
    let top = padTop;
    return pageHeights.map((height) => {
      const offset = top;
      top += height + gap;
      return offset;
    });
  }, [pageHeights]);

  const updateRenderWindow = useCallback(() => {
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport || !pageOffsets.length) return;

    const { scrollTop, clientHeight } = viewport;
    // Last page starting at or above the position
    const pageAt = (position: number) => {
      let page = 1;
      for (let index = 0; index < pageOffsets.length; index += 1) {
        if (pageOffsets[index] > position) break;
        page = index + 1;
      }
      return page;
    };
    const firstVisible = pageAt(scrollTop);
    const lastVisible = pageAt(scrollTop + clientHeight);
    const start = Math.max(1, firstVisible - renderBuffer);
    const end = Math.min(pagesCount, lastVisible + renderBuffer);

    setRenderRange((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end }
    );
  }, [wrapperRef, pageOffsets, pagesCount]);

  useEffect(() => {
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return undefined;

    const frameId = requestAnimationFrame(updateActivePageFromScroll);

    viewport.addEventListener('scroll', updateActivePageFromScroll, {
      passive: true
    });
    window.addEventListener('resize', updateActivePageFromScroll);

    return () => {
      cancelAnimationFrame(frameId);
      viewport.removeEventListener('scroll', updateActivePageFromScroll);
      window.removeEventListener('resize', updateActivePageFromScroll);
    };
  }, [wrapperRef, updateActivePageFromScroll, pageRefsVersion]);

  useEffect(() => {
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return undefined;

    updateRenderWindow();

    viewport.addEventListener('scroll', updateRenderWindow, { passive: true });
    window.addEventListener('resize', updateRenderWindow);

    return () => {
      viewport.removeEventListener('scroll', updateRenderWindow);
      window.removeEventListener('resize', updateRenderWindow);
    };
  }, [wrapperRef, updateRenderWindow, isPageSizeReady]);

  useEffect(() => {
    if (activePageFromScrollRef.current) {
      activePageFromScrollRef.current = false;
      lastScrolledPageRef.current = activePageNumber;
      return;
    }

    if (activePageNumber === lastScrolledPageRef.current) return;
    lastScrolledPageRef.current = activePageNumber;

    if (scrollPdfToPage(activePageNumber)) {
      requestAnimationFrame(() => {
        runCanvasLayout();
      });
    }
  }, [activePageNumber, scrollPdfToPage, runCanvasLayout]);

  useEffect(() => {
    if (pendingScrollToPageRef.current == null) return;
    if (scrollPdfToPage(pendingScrollToPageRef.current)) {
      requestAnimationFrame(() => {
        runCanvasLayout();
      });
    }
  }, [pageRefsVersion, scrollPdfToPage, runCanvasLayout]);

  // Makes a page in view active (e.g. the one under the cursor), without
  // scrolling to it like for other active page changes
  const setActivePageInView = useCallback(
    (pageNumber: number) => {
      if (pageNumber === activePageNumber) return;
      activePageFromScrollRef.current = true;
      lastScrolledPageRef.current = pageNumber;
      setActivePageNumber(pageNumber);
    },
    [activePageNumber, setActivePageNumber]
  );

  return {
    renderRange,
    visiblePageNumber,
    updateActivePageFromScroll,
    setActivePageInView
  };
}
